import React from "react";
import { Link } from "react-router-dom";

/**
 * Session 60.10 · SocialLinks
 *
 * Renders the platform's social icons as clickable links. Currently:
 *   · Instagram → @workmetier
 *
 * Adding a new network is a one-liner in the SOCIAL_LINKS array below;
 * every render surface picks it up automatically. Icons are inline SVG so
 * they inherit currentColor for theming and never depend on an external
 * icon font at runtime.
 */
const SOCIAL_LINKS = [
  {
    key: "instagram",
    label: "Métier on Instagram",
    handle: "@workmetier",
    href: "https://www.instagram.com/workmetier",
    Icon: (props) => (
      <svg viewBox="0 0 24 24" width={props.size} height={props.size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
];

export function SocialLinks({ size = 20, variant = "footer", className = "" }) {
  const gap = variant === "contact" ? 10 : 8;
  const iconPad = variant === "contact" ? 10 : 8;
  return (
    <div
      className={`m-social-links ${className}`}
      data-testid="social-links"
      style={{ display: "inline-flex", gap, alignItems: "center", flexWrap: "wrap" }}
    >
      {SOCIAL_LINKS.map(({ key, label, href, handle, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={handle}
          data-testid={`social-${key}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: iconPad,
            borderRadius: 999,
            border: "1px solid rgba(184,145,58,0.35)",
            color: "var(--gold, #B8913A)",
            background: "transparent",
            textDecoration: "none",
            transition: "background 120ms ease, color 120ms ease, transform 120ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(184,145,58,0.10)";
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Icon size={size} />
        </a>
      ))}
    </div>
  );
}

/**
 * ContactBlock — self-contained "Contact Us" section that reads on any page.
 * Reuses SocialLinks so the social icons stay consistent across surfaces.
 */
export function ContactBlock({
  eyebrow = "Contact us",
  heading = "Say hello.",
  email = "contact@workmetier.com",
  copy = "Real humans read every message. Reach the team by email, or follow along for what's new.",
  align = "left",
  variant = "light", // "light" = navy text on cream, "dark" = cream text on navy
}) {
  const isDark = variant === "dark";
  return (
    <section
      className="m-contact-block"
      data-testid="contact-block"
      style={{
        padding: "22px 22px",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(255,248,228,0.14)" : "rgba(27,35,64,0.10)"}`,
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(184,145,58,0.06)",
        textAlign: align,
        maxWidth: 560,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--gold, #B8913A)",
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        {eyebrow}
      </div>
      <h3
        style={{
          fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
          fontWeight: 500,
          fontSize: 26,
          margin: "4px 0 8px",
          color: isDark ? "#FAF8F3" : "var(--m-navy, #1B2340)",
        }}
        data-testid="contact-block-heading"
      >
        {heading}
      </h3>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.6,
          margin: 0,
          color: isDark ? "rgba(250,248,243,0.75)" : "rgba(27,35,64,0.75)",
        }}
      >
        {copy}
      </p>
      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12, alignItems: align === "center" ? "center" : "flex-start" }}>
        <Link
          to="/contact"
          data-testid="contact-block-cta"
          style={{
            display: "inline-block",
            padding: "10px 18px",
            borderRadius: 999,
            background: "linear-gradient(135deg,#D6B559 0%,#B8913A 55%,#8B6A22 100%)",
            color: "#FFF8E4",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            textDecoration: "none",
            border: "1px solid rgba(139,106,34,0.55)",
          }}
        >
          Send us a message &rarr;
        </Link>
        <a
          href={`mailto:${email}`}
          data-testid="contact-block-email"
          style={{
            fontSize: 13,
            color: isDark ? "rgba(250,248,243,0.75)" : "rgba(27,35,64,0.75)",
            textDecoration: "none",
          }}
        >
          or email {email} &rarr;
        </a>
        {/* Session 60.15 · Text link reads better than a lone icon on this
            small aside block, mirroring the /contact page treatment. */}
        <a
          href="https://www.instagram.com/workmetier"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-block-instagram"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: isDark ? "#E4CE8A" : "#B8913A",
            textDecoration: "none",
            borderBottom: `1px solid ${isDark ? "rgba(228,206,138,0.4)" : "rgba(184,145,58,0.4)"}`,
            paddingBottom: 1,
          }}
        >
          Follow us on Instagram &rarr;
        </a>
      </div>
    </section>
  );
}

export default SocialLinks;
